import React, { useState, useEffect, useRef } from 'react';
import Layout from "../components/layout/LegalLayout.jsx";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Button
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  AccessTime as AccessTimeIcon,
  PriorityHigh as PriorityHighIcon,
  Add as AddIcon,
  Gavel as GavelIcon
} from "@mui/icons-material";
import Chart from 'chart.js/auto';
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom"; 
import config from "../config.js";

const Legal = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalCases: 0,
    activeCases: 0,
    highPriorityCases: 0
  });
  
  const statusChartRef = useRef(null);
  const typeChartRef = useRef(null);
  const priorityChartRef = useRef(null);
  const timelineChartRef = useRef(null);
  
  const charts = useRef({});
  const { API_BASE_URL } = config;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);
  const navigate = useNavigate(); 

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/legalcases/${selectedCompanyId}`);
      setCases(response.data.data || []);
      calculateMetrics(response.data.data);
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (casesData) => {
    const active = casesData.filter(c => c.status !== 'Completed').length;
    const highPriority = casesData.filter(c => c.priority === 'High').length;

    setMetrics({
      totalCases: casesData.length,
      activeCases: active,
      highPriorityCases: highPriority
    });
  };

  const handleNavigateToNewCase = () => {
    navigate('/newcase'); 
  };

  const createCharts = () => {
   
    Object.values(charts.current).forEach(chart => chart?.destroy());

   
    const statusCtx = statusChartRef.current.getContext('2d');
    const statusData = processStatusData(cases);
    charts.current.status = new Chart(statusCtx, {
      type: 'pie',
      data: {
        labels: statusData.labels,
        datasets: [{
          data: statusData.values,
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)'
          ]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right'
          },
          title: {
            display: true,
            text: 'Case Status Distribution'
          }
        }
      }
    });


    const typeCtx = typeChartRef.current.getContext('2d');
    const typeData = processTypeData(cases);
    charts.current.type = new Chart(typeCtx, {
      type: 'doughnut',
      data: {
        labels: typeData.labels,
        datasets: [{
          data: typeData.values,
          backgroundColor: [
            'rgba(255, 99, 132, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)'
          ]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right'
          },
          title: {
            display: true,
            text: 'Case Type Distribution'
          }
        }
      }
    });

    const priorityCtx = priorityChartRef.current.getContext('2d');
    const priorityData = processPriorityData(cases);
    charts.current.priority = new Chart(priorityCtx, {
      type: 'bar',
      data: {
        labels: priorityData.labels,
        datasets: [{
          label: 'Number of Cases',
          data: priorityData.values,
          backgroundColor: [
            'rgba(255, 99, 132, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(75, 192, 192, 0.7)'
          ]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Cases by Priority'
          }
        },
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    });

   
    const timelineCtx = timelineChartRef.current.getContext('2d');
    const timelineData = processTimelineData(cases);
    charts.current.timeline = new Chart(timelineCtx, {
      type: 'line',
      data: {
        labels: timelineData.labels,
        datasets: [{
          label: 'Cases Opened',
          data: timelineData.values,
          borderColor: 'rgba(75, 192, 192, 1)',
          tension: 0.1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Cases Opened Over Time'
          }
        },
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    });
  };

  const processStatusData = (casesData) => {
    const statusCount = casesData.reduce((acc, curr) => {
      acc[curr.status] = (acc[curr.status] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(statusCount),
      values: Object.values(statusCount)
    };
  };

  const processTypeData = (casesData) => {
    const typeCount = casesData.reduce((acc, curr) => {
      acc[curr.type] = (acc[curr.type] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(typeCount),
      values: Object.values(typeCount)
    };
  };

  const processPriorityData = (casesData) => {
    const priorityCount = casesData.reduce((acc, curr) => {
      acc[curr.priority] = (acc[curr.priority] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: ['High', 'Medium', 'Low'],
      values: ['High', 'Medium', 'Low'].map(p => priorityCount[p] || 0)
    };
  };

  const processTimelineData = (casesData) => {
    const monthlyCount = casesData.reduce((acc, curr) => {
      const date = new Date(curr.openDate);
      const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
      acc[monthYear] = (acc[monthYear] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(monthlyCount),
      values: Object.values(monthlyCount)
    };
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCases();
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!loading && cases.length > 0) {
      createCharts();
    }
    return () => {
      Object.values(charts.current).forEach(chart => chart?.destroy());
    };
  }, [cases, loading]);

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  
  if (!loading && cases.length === 0) {
    return (
      <Layout>
        <Box sx={{ flexGrow: 1, backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
          <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              minHeight="60vh"
              textAlign="center"
            >
              <Card elevation={3} sx={{ p: 4, maxWidth: 500 }}>
                <CardContent>
                  <GavelIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h4" gutterBottom color="text.secondary">
                    No Legal Cases Found
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    It looks like there are no legal cases for this company yet. 
                    Get started by creating your first case.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<AddIcon />}
                    onClick={handleNavigateToNewCase}
                    sx={{ mt: 2 }}
                  >
                    Create New Case
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Container>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            {/* Metrics Cards */}
            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <AssessmentIcon sx={{ mr: 2, color: "primary.main" }} />
                    <Box>
                      <Typography variant="subtitle1">Total Cases</Typography>
                      <Typography variant="h5">{metrics.totalCases}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <AccessTimeIcon sx={{ mr: 2, color: "success.main" }} />
                    <Box>
                      <Typography variant="subtitle1">Active Cases</Typography>
                      <Typography variant="h5">{metrics.activeCases}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <PriorityHighIcon sx={{ mr: 2, color: "error.main" }} />
                    <Box>
                      <Typography variant="subtitle1">High Priority Cases</Typography>
                      <Typography variant="h5">{metrics.highPriorityCases}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Charts */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2, height: '400px' }}>
                <Box sx={{ height: '100%' }}>
                  <canvas ref={statusChartRef} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2, height: '400px' }}>
                <Box sx={{ height: '100%' }}>
                  <canvas ref={typeChartRef} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2, height: '400px' }}>
                <Box sx={{ height: '100%' }}>
                  <canvas ref={priorityChartRef} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2, height: '400px' }}>
                <Box sx={{ height: '100%' }}>
                  <canvas ref={timelineChartRef} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Layout>
  );
};

export default Legal;
